import { isProductionMode } from '@/config/axois.config';
import { API_URLS } from './constant';
import { GameType } from '@/utils/index.model';
import { getBaseUrl, getFightDataFromBinary } from './helpers';
import store from '@/store';
import * as protobuf from 'protobufjs';

const WSURLConstants = {
  gameClientNotifications: () => 'game-client/notifications/',
  [GameType.fights]: 'fights',
  [GameType.fight_results]: 'fight_results',
  [GameType.arena_stream]: 'arena_stream',
  [GameType.main]: 'main',
};

// const debug = store.getters['general/debug'];
// const debug = true;

const getArenaId = () => store.getters['general/selectedArena']?.id;

const isBinaryDisabled = () => store.getters['general/binaryDisabled'];

interface WsRetryTracker {
  [key: string]: number;
}

interface WsPayloads {
  matchDayId?: string;
  oddsType?: string;
  id?: string;
  lang?: string;
  platform?: string;
  drmType?: string;
}

const wsRetryTracker: WsRetryTracker = {};
let resetReconnectionInterval: any = '';
let mainConnection: IWS | null = null;

// Define the protobuf message using the proto string
const protoDefinition = `
syntax = "proto3";

package gcproto;

option go_package = "internal/gcproto";

enum ResponseMessageType {
    ARENA_RESPONSE_MESSAGE_TYPE = 0;
    FIGHT_RESPONSE_MESSAGE_TYPE = 1;
    BALANCE_RESPONSE_MESSAGE_TYPE = 2;
    MEMBER_JACKPOT_POOL_RESPONSE_MESSAGE_TYPE = 3;
    ARENA_STREAM_RESPONSE_MESSAGE_TYPE = 4;
    FIGHT_RESULT_RESPONSE_MESSAGE_TYPE = 5;
    ARENA_VISIBLITY_UPDATED_RESPONSE_MESSAGE_TYPE = 6;
    FIGHT_V2_RESPONSE_MESSAGE_TYPE = 7;
    FIGHT_ODDS_RESPONSE_MESSAGE_TYPE = 8;
    REFRESH_STREAM_URL_MESSAGE_TYPE = 9;
    REFRESH_ANNOUNCEMENT_RESPONSE_MESSAGE_TYPE = 10;
}

enum RequestMessageType {
    ARENA_STREAM_REQUEST_MESSAGE_TYPE = 0;
    FIGHT_RESULT_REQUEST_MESSAGE_TYPE = 1;
}

enum ErrorCode {
    NO_ERROR = 0;
    UNDEFINED_ERROR = 1;
    STREAM_MIN_BALANCE_ERROR = 2;
}

message Arena {
    string id = 1;
    string name = 2;
    string country = 3;
    string code = 4;
    int32 status = 5;
    message MatchDay {
        string id = 1;
        string code =2;
        uint64 start_ts = 3;
        uint64 end_ts = 4;
        string fight_type = 5;
        bool is_reverse = 6;
    }
}

message Fight {
    string id = 1;
    uint32 fight_number = 2;
    string status = 3;
    string result = 4;
    string replay_url = 5;
    bool is_bets_paused = 6;
    uint64 confirmed_ts = 7;
    uint64 started_ts = 8;
    string banker_display_name = 9;
    double banker_odds = 10;
    double banker_total_amount = 11;
    uint32 banker_total_bet_count = 12;
    string player_display_name = 13;
    double player_odds = 14;
    double player_total_amount = 15;
    uint32 player_total_bet_count = 16;
    string draw_display_name = 17;
    double draw_odds = 18;
    double draw_total_amount = 19;
    uint32 draw_total_bet_count = 20;
    string arena_id = 21;
    string match_day_id = 22;
    bool is_reverse = 23;
}

message FightV2 {
    string id = 1;
    uint32 fight_number = 2;
    uint32 status = 3;
    string match_day_id = 4;
    bool is_reverse = 5;
    string arena_id = 6;
    string banker_name = 7;
    string player_name = 8;
    string draw_name = 9;
    uint64 confirmed_ts = 10;
    uint64 started_ts = 11;
    string result = 12;
    bool has_draw_selection = 13;
}

message FightOdds {
    string fight_id = 1;
    double banker_odds = 2;
    uint32 banker_count = 3;
    double banker_amount = 4;
    double player_odds = 5;
    uint32 player_count = 6;
    double player_amount = 7;
    double draw_odds = 8;
    uint32 draw_count = 9;
    double draw_amount = 10;
}

message Balance {
    double balance = 1;
}

message MemberJackpotPool {
    int32 type = 1;
    double next_ticket_amount = 2;
    double current_ticket_amount = 3;
}

message ResponseMessage {
    ResponseMessageType type = 1; 
    oneof data {
        Arena arena = 2;
        Fight fight = 3;
        Balance balance = 4;
        MemberJackpotPool member_jackpot_pool = 5;
        ArenaStream arena_stream = 6;
        FightResult fight_result =7;
        FightV2 fightv2 = 10;
        FightOdds fight_odds = 11;
    }
    ErrorCode error_code = 8;
    string error_message = 9;
}

message RequestMessage {
    RequestMessageType type = 1;
    oneof data {
        RequestArenaStream arena = 2;
        RequestFightResult fight_result = 3;
    }
}

message RequestArenaStream {
    string id = 1;
    string lang = 2;
    string platform = 3;
    string drm_type = 4;
}

message RequestFightResult {
    string match_day_id = 1;
    string odds_type = 2;
}

message ArenaStream {
    string url = 1;
    string watermark = 2;
    double watermark_frequency = 3;
    uint32 watermark_movement = 4;
    uint32 watermark_type = 5;
    string watermark_image_url = 6;
    uint32 watermark_font_size = 7;
    string drm_license_url = 8;
    string drm_cert_url = 9;
}

message FightResultItem {
    string id = 1;
    uint32 fight_number = 2;
    string status = 3;
    string result = 4;
    uint64 confirmed_date_time_ts = 5;
    string banker_display_name = 6;
    string player_display_name = 7;
    string draw_display_name = 8;
    double banker_odds = 9;
    double player_odds = 10;
    double draw_odds = 11;
}

message FightResult {
    uint32 banker_count = 1;
    uint32 player_count = 2;
    uint32 draw_count = 3;
    repeated FightResultItem fights = 4;
}
`;

// Load the proto definition
const root = protobuf.parse(protoDefinition).root;
const Message = root.lookupType('gcproto.ResponseMessage');
const RequestMessage = root.lookupType('gcproto.RequestMessage');
const requestType = root.lookupEnum('gcproto.RequestMessageType');

const baseServer = getBaseUrl();
const isProd = isProductionMode || process.env.NODE_ENV === 'production';

const wsUrl = isProd
  ? API_URLS.WEBSOCKET_URL.replace('ga28-api.com', baseServer)
  : API_URLS.WEBSOCKET_URL_DEV.replace('ga28-api.com', baseServer);

const connectToFights = (
  onReceive: (data: any) => void,
  reconnectHandler: () => void,
  token: string,
  oddsType: string,
  gameType: GameType,
  arenaIdOverride?: string,
  requestName: string = gameType
) => {
  const request = wsUrl + WSURLConstants[gameType];
  // if (debug) {
  //   request = wsUrl + WSURLConstants[gameType];
  // } else {
  //   request = wsUrl + 'fight-updates';
  // }
  const arenaId = arenaIdOverride || getArenaId();

  if (!arenaId) return null;

  const wss = WebSocketService(
    `${request}?token=${token}&odds_type=${oddsType}&arena_id=${arenaId}`,
    requestName
  );
  wss.executeHandlers(onReceive, reconnectHandler);
  return wss;
};

const connectToMain = (
  onReceive: (data: any) => void,
  reconnectHandler: () => void,
  token: string,
  gameType: GameType
) => {
  if (mainConnection) {
    // Update handlers for the existing connection
    mainConnection.executeHandlers(onReceive, reconnectHandler);
    return mainConnection;
  }

  const request = wsUrl + 'main';
  mainConnection = WebSocketService(`${request}?token=${token}`, gameType);
  mainConnection.executeHandlers(onReceive, reconnectHandler);
  return mainConnection;
};

const sendMainRequest = (onReceive: (data: any) => void, token: string, gameType: GameType, payload: WsPayloads) => {
  // Ensure main connection exists
  const reconnectHandler = () => {
    mainConnection = null;
    connectToMain(onReceive, reconnectHandler, token, gameType);
  };
  const wss = connectToMain(onReceive, reconnectHandler, token, gameType);

  // Create the appropriate request message based on gameType
  let requestData: Uint8Array;
  if (gameType === GameType.fight_results) {
    // Use a lower-level approach to ensure correct encoding
    const writer = new protobuf.Writer();
    writer.uint32(8).int32(requestType.values['FIGHT_RESULT_REQUEST_MESSAGE_TYPE']); // Field 1: type

    // Write the fight_result message (field 3)
    const fightResultWriter = new protobuf.Writer();
    if (payload.matchDayId && payload.oddsType) {
      fightResultWriter.uint32(10).string(payload.matchDayId); // Field 1: match_day_id
      fightResultWriter.uint32(18).string(payload.oddsType); // Field 2: odds_type
    }

    // Add the nested message
    writer.uint32(26).bytes(fightResultWriter.finish()); // Field 3: fight_result

    requestData = writer.finish();
  } else if (gameType === GameType.arena_stream) {
    const requestMessage = {
      type: requestType.values['ARENA_STREAM_REQUEST_MESSAGE_TYPE'],
      arena: {
        id: payload.id,
        lang: payload.lang,
        platform: payload.platform,
        drmType: payload.drmType,
      },
    };
    const message = RequestMessage.create(requestMessage);
    requestData = RequestMessage.encode(message).finish();
  } else {
    throw new Error('Unsupported game type for main request');
  }

  // Send the request over the existing connection
  wss.sendMessage(requestData);
  return wss;
};

const WebSocketService = (request: string, requestName: string) => {
  const socket = new WebSocket(request);
  let clientSideClose = false;
  let clearTimeoutHandler: () => void;
  let pendingMessages: any[] = [];

  const sendPendingMessages = () => {
    while (pendingMessages.length > 0 && socket.readyState === WebSocket.OPEN) {
      const message = pendingMessages.shift();
      socket.send(message);
    }
  };

  return {
    executeHandlers: (onReceive: (data: any) => void, reconnectHandler: () => void) => {
      socket.onopen = () => {
        if (resetReconnectionInterval) clearTimeout(resetReconnectionInterval);
        resetReconnectionInterval = setTimeout(() => (wsRetryTracker[requestName] = 1), 300000);
        sendPendingMessages(); // Send any queued messages
      };
      socket.onmessage = async (e: any) => {
        try {
          if (e.data instanceof Blob) {
            const arrayBuffer = await e.data.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);
            const decodedMessage = Message.decode(uint8Array);
            const jsonMessage = Message.toObject(decodedMessage, {
              longs: String,
              enums: String,
              bytes: String,
              defaults: true,
              arrays: true,
              objects: true,
              oneofs: true,
            });
            
            if(isBinaryDisabled()) {
              console.log(jsonMessage)
            }

            const messageType = jsonMessage.type;
            let processedData = {
              type: '',
              data: {},
              errorCode: '',
              errorMessage: '',
            };
            switch (messageType) {
              case 0:
              case '0':
              case 'ARENA_RESPONSE_MESSAGE_TYPE':
                processedData.type = 'arena';
                processedData.errorCode = jsonMessage.errorCode;
                processedData.errorMessage = jsonMessage.errorMessage;
                processedData.data = jsonMessage.arena;
                break;
              case 1:
              case '1':
              case 'FIGHT_RESPONSE_MESSAGE_TYPE':
                processedData.type = 'fight';
                processedData.errorCode = jsonMessage.errorCode;
                processedData.errorMessage = jsonMessage.errorMessage;
                processedData.data = getFightDataFromBinary(jsonMessage.fight);
                if (jsonMessage.fight && jsonMessage.fight.status) {
                  store.dispatch('fight/updateFightStatus', {
                    status: jsonMessage.fight.status,
                    fightNumber: jsonMessage.fight.fightNumber,
                    arenaId: jsonMessage.fight?.arenaId,
                  });
                }
                break;
              case 2:
              case '2':
              case 'BALANCE_RESPONSE_MESSAGE_TYPE':
                processedData.type = 'balance';
                processedData.errorCode = jsonMessage.errorCode;
                processedData.errorMessage = jsonMessage.errorMessage;
                processedData.data = jsonMessage.balance;
                break;
              case 3:
              case '3':
              case 'MEMBER_JACKPOT_POOL_RESPONSE_MESSAGE_TYPE':
                processedData.type = 'member_jackpot_pool';
                processedData.errorCode = jsonMessage.errorCode;
                processedData.errorMessage = jsonMessage.errorMessage;
                processedData.data = jsonMessage.memberJackpotPool;
                break;
              case 4:
              case '4':
              case 'ARENA_STREAM_RESPONSE_MESSAGE_TYPE':
                processedData.type = 'arena_stream';
                processedData.errorCode = jsonMessage.errorCode;
                processedData.errorMessage = jsonMessage.errorMessage;
                processedData.data = jsonMessage.arenaStream;
                break;
              case 5:
              case '5':
              case 'FIGHT_RESULT_RESPONSE_MESSAGE_TYPE':
                processedData.type = 'fight_result';
                processedData.errorCode = jsonMessage.errorCode;
                processedData.errorMessage = jsonMessage.errorMessage;
                processedData.data = jsonMessage.fightResult;
                break;
              case 6:
              case '6':
              case 'ARENA_VISIBLITY_UPDATED_RESPONSE_MESSAGE_TYPE':
                processedData.type = 'arena_visibility_updated';
                processedData.errorCode = jsonMessage.errorCode;
                processedData.errorMessage = jsonMessage.errorMessage;
                break;
              case 7:
              case '7':
              case 'FIGHT_V2_RESPONSE_MESSAGE_TYPE':
                processedData.type = 'fight_v2';
                processedData.errorCode = jsonMessage.errorCode;
                processedData.errorMessage = jsonMessage.errorMessage;
                processedData.data = getFightDataFromBinary(jsonMessage.fightv2);
                break;
              case 8:
              case '8':
              case 'FIGHT_ODDS_RESPONSE_MESSAGE_TYPE':
                processedData.type = 'fight_odds';
                processedData.errorCode = jsonMessage.errorCode;
                processedData.errorMessage = jsonMessage.errorMessage;
                processedData.data = getFightDataFromBinary(jsonMessage.fightOdds);
                break;
              case 9:
              case '9':
              case 'REFRESH_STREAM_URL_MESSAGE_TYPE':
                processedData.type = 'refresh_stream_url';
                processedData.errorCode = jsonMessage.errorCode;
                processedData.errorMessage = jsonMessage.errorMessage;
                break;
              case 10:
              case '10':
              case 'REFRESH_ANNOUNCEMENT_RESPONSE_MESSAGE_TYPE':
                processedData.type = 'refresh_announcement';
                processedData.errorCode = jsonMessage.errorCode;
                processedData.errorMessage = jsonMessage.errorMessage;
                break;
              default:
                console.warn('Unknown message type:', messageType);
            }
            onReceive(processedData);
          } else {
            const dataFight = JSON.parse(e.data);
            if (dataFight.data && dataFight.data.status) {
              store.dispatch('fight/updateFightStatus', {
                status: dataFight.data.status,
                fightNumber: dataFight.data.fight_number,
                arenaId: dataFight.data?.match_day?.arena?.id || dataFight.data?.id,
              });
            }
            onReceive(JSON.parse(e.data));
          }
        } catch (error) {
          console.error('Error processing WebSocket message:', error);
        }
      };
      socket.onerror = (error: any) => {
        console.log(requestName, 'error:', error);
      };
      socket.onclose = (e: any) => {
        if (clientSideClose) {
          console.log(requestName, 'will be closed');
        } else if (reconnectHandler) {
          console.log(requestName, `will reconnect! in ${wsRetryTracker[requestName] || 1} seconds`);
          const timeoutID = setTimeout(() => {
            reconnectHandler();
          }, 1000 * (wsRetryTracker[requestName] || 1));

          if ((wsRetryTracker[requestName] || 1) < 8) {
            wsRetryTracker[requestName] = (wsRetryTracker[requestName] || 1) * 2;
          }

          clearTimeoutHandler = () => {
            clearTimeout(timeoutID);
            console.log(requestName, 'clear timeout id', timeoutID);
          };
          console.log(requestName, 'timeout id', timeoutID);
          console.log(requestName, 'close code:', e.code, ' reason:', e);
        } else {
          console.log(requestName, 'close code:', e.code, ' reason:', e.reason);
        }
      };
    },
    disconnect: () => {
      console.log(requestName, 'disconnect');
      if (clearTimeoutHandler) {
        clearTimeoutHandler();
      }
      clientSideClose = true;
      socket.close();
      if (requestName === GameType.main) {
        mainConnection = null;
        pendingMessages = []; // Clear pending messages on disconnect
      }
    },
    sendMessage: (data: any) => {
      if (!clientSideClose && socket.readyState === WebSocket.OPEN) {
        socket.send(data);
      } else if (!clientSideClose) {
        // console.log(`${requestName} WebSocket is not open, queuing message`);
        pendingMessages.push(data);
      } else {
        console.warn(`${requestName} WebSocket is closed, cannot queue message`);
      }
    },
  };
};

interface IWS {
  executeHandlers: (onReceive: (data: any) => void, reconnectHandler: () => void) => void;
  disconnect: () => void;
  sendMessage: (data: any) => void;
}

/**
 * Send security event to backend
 */
const sendSecurityEvent = (
  token: string,
  event: {
    eventType: string;
    userId: string;
    sessionId: string;
    timestamp: number;
    details: string;
    severity: string;
  }
) => {
  if (!mainConnection) {
    console.warn('[WebSocket] No main connection available for security event');
    return;
  }

  try {
    const writer = new protobuf.Writer();
    writer.uint32(8).int32(requestType.values['SECURITY_EVENT_REQUEST_MESSAGE_TYPE']); // Field 1: type

    // Write the security_event message (field 4)
    const securityEventWriter = new protobuf.Writer();
    securityEventWriter.uint32(10).string(event.eventType); // Field 1: event_type
    securityEventWriter.uint32(18).string(event.userId); // Field 2: user_id
    securityEventWriter.uint32(26).string(event.sessionId); // Field 3: session_id
    securityEventWriter.uint32(32).uint64(event.timestamp); // Field 4: timestamp
    securityEventWriter.uint32(42).string(event.details); // Field 5: details
    securityEventWriter.uint32(50).string(event.severity); // Field 6: severity

    // Add the nested message
    writer.uint32(34).bytes(securityEventWriter.finish()); // Field 4: security_event

    const requestData = writer.finish();

    // Send the request
    mainConnection.sendMessage(requestData);
  } catch (error) {
    console.error('[WebSocket] Error sending security event:', error);
  }
};

export { connectToFights, connectToMain, sendMainRequest, sendSecurityEvent, IWS };
